
import { useEffect, useRef, useState, useCallback } from 'react';

interface UseSmartScrollOptions {
  messages: any[];
  isLoadingHistory: boolean;
  currentSessionId?: string;
  threshold?: number;
}

export const useSmartScroll = ({
  messages,
  isLoadingHistory,
  currentSessionId,
  threshold = 50
}: UseSmartScrollOptions) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const previousMessageCountRef = useRef(messages.length);
  const previousSessionIdRef = useRef(currentSessionId);
  const isInitialMountRef = useRef(true);
  const [isNearBottom, setIsNearBottom] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);

  // Check if user is near the bottom of the scroll container
  const checkScrollPosition = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const nearBottom = distanceFromBottom <= threshold;
    
    setIsNearBottom(nearBottom);
    setShowScrollButton(!nearBottom && messages.length > 0);
  }, [threshold, messages.length]);

  // Scroll to bottom with controlled scrollTop
  const scrollToBottom = useCallback((smooth: boolean = true) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    if (smooth) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth'
      });
    } else {
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  // Handle scroll events to track user position
  const handleScroll = useCallback(() => {
    checkScrollPosition();
  }, [checkScrollPosition]);

  // Auto-scroll logic with smart conditions
  useEffect(() => {
    // Skip auto-scroll on initial mount
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      previousMessageCountRef.current = messages.length;
      previousSessionIdRef.current = currentSessionId;
      // Scroll to bottom on initial load without animation
      setTimeout(() => scrollToBottom(false), 100);
      return;
    }

    // Skip auto-scroll when loading history
    if (isLoadingHistory) {
      previousMessageCountRef.current = messages.length;
      previousSessionIdRef.current = currentSessionId;
      return;
    }

    // On session change, scroll to bottom
    if (currentSessionId !== previousSessionIdRef.current) {
      previousMessageCountRef.current = messages.length;
      previousSessionIdRef.current = currentSessionId;
      setTimeout(() => scrollToBottom(false), 100);
      return;
    }

    // Only auto-scroll if new messages were added AND user is near bottom
    if (messages.length > previousMessageCountRef.current && isNearBottom) {
      scrollToBottom(true);
    }
    
    previousMessageCountRef.current = messages.length;
    previousSessionIdRef.current = currentSessionId;
  }, [messages, isLoadingHistory, currentSessionId, isNearBottom, scrollToBottom]);

  // Update scroll position when container size changes
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver(() => {
      checkScrollPosition();
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [checkScrollPosition]);

  return {
    scrollContainerRef,
    showScrollButton,
    scrollToBottom: () => scrollToBottom(true),
    handleScroll
  };
};
